import { frankencoinChainData, frankencoinConfigFor } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'

/**
 * User-data call build for Frankencoin. Positions are individual CONTRACTS —
 * a user may own many per market — so this is a DISCOVERY-first build
 * (async, same shape as Teller's bidId scan), but the discovery is unusually
 * cheap: Frankencoin's public API returns the ENTIRE book (72 positions at
 * integration) in one call, so ONE shared, cached fetch serves every account
 * and costs zero RPC.
 *
 *  1. fetch `/positions/open` once per TTL, index it by owner;
 *  2. keep positions owned by the account whose `original` is in our curated
 *     market roster (the metadata generator drops V1 and unpriceable
 *     collateral — a position we cannot price must not surface as a
 *     position we can);
 *  3. data phase per kept position: `owner` (re-verified on-chain — the API
 *     is a pointer, never the source of truth), `minted`, `price`,
 *     `expiration`, `challengedAmount`, and the collateral `balanceOf`.
 *
 * Deliberately NOT an on-chain event scan: our RPC notes are unambiguous
 * that free endpoints silently return empty `eth_getLogs` results, which
 * would look exactly like "user has no positions".
 */
export const FRANKENCOIN_CALLS_PER_POSITION = 6

const BOOK_TTL_MS = 5 * 60_000
const DISCOVERY_TTL_MS = 5 * 60_000
const API_BASE = 'https://api.frankencoin.com'

interface BookEntry {
  position: string
  owner: string
  original: string
  version: number
  closed: boolean
  denied: boolean
}

let bookCache: { at: number; entries: BookEntry[] } | undefined

/** The whole open book, cached across accounts. */
const fetchBook = async (): Promise<BookEntry[]> => {
  if (bookCache && Date.now() - bookCache.at < BOOK_TTL_MS) {
    return bookCache.entries
  }
  const res = await fetch(`${API_BASE}/positions/open`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`frankencoin book: ${res.status}`)
  const data = (await res.json()) as any
  const entries: BookEntry[] = Object.values(data?.map ?? {}).map((p: any) => ({
    position: String(p.position).toLowerCase(),
    owner: String(p.owner).toLowerCase(),
    // An original is its own parent.
    original: String(p.original ?? p.position).toLowerCase(),
    version: Number(p.version),
    closed: !!p.closed,
    denied: !!p.denied,
  }))
  bookCache = { at: Date.now(), entries }
  return entries
}

export interface FrankencoinDiscoveredPosition {
  /** The clone (or original) contract — the sub-account id. */
  position: string
  /** The ORIGINAL this position belongs to — selects the market key. */
  original: string
}

export interface FrankencoinDiscovery {
  positions: FrankencoinDiscoveredPosition[]
  at: number
}

const discoveryCache = new Map<string, FrankencoinDiscovery>()
const cacheKey = (chainId: string, account: string) =>
  `${chainId}:${account.toLowerCase()}`

export const getCachedFrankencoinPositions = (
  chainId: string,
  account: string,
): FrankencoinDiscovery | undefined => {
  const hit = discoveryCache.get(cacheKey(chainId, account))
  if (!hit || Date.now() - hit.at > DISCOVERY_TTL_MS) return undefined
  return hit
}

export const buildFrankencoinUserCall = async (
  chainId: string,
  lender: string,
  account: string,
): Promise<Call[]> => {
  const cfg = frankencoinConfigFor(lender, chainId)
  const markets = frankencoinChainData(lender, chainId)?.markets ?? []
  const stash = (d: FrankencoinDiscovery) =>
    discoveryCache.set(cacheKey(chainId, account), d)

  if (!cfg || markets.length === 0) {
    stash({ positions: [], at: Date.now() })
    return []
  }
  const curated = new Set(markets.map((m) => m.position.toLowerCase()))
  const lower = account.toLowerCase()

  let book: BookEntry[] = []
  try {
    book = await fetchBook()
  } catch {
    // Discovery failure ⇒ report nothing rather than a fake empty book; the
    // per-account TTL keeps a prior good discovery alive for 5 minutes.
    return []
  }

  const positions = book
    .filter(
      (e) =>
        e.owner === lower &&
        e.version === 2 &&
        !e.closed &&
        !e.denied &&
        curated.has(e.original),
    )
    .map((e) => ({ position: e.position, original: e.original }))

  stash({ positions, at: Date.now() })
  if (positions.length === 0) return []

  const collByOriginal = new Map(
    markets.map((m) => [m.position.toLowerCase(), m.collToken]),
  )

  const calls: Call[] = []
  for (const p of positions) {
    const coll = collByOriginal.get(p.original)!
    calls.push(
      { address: p.position, name: 'owner', params: [] },
      { address: p.position, name: 'minted', params: [] },
      { address: p.position, name: 'price', params: [] },
      { address: p.position, name: 'expiration', params: [] },
      { address: p.position, name: 'challengedAmount', params: [] },
      { address: coll, name: 'balanceOf', params: [p.position] },
    )
  }
  return calls
}
